// src/components/settings/AISettings.js
import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import './SettingsPanel.css';

const AISettings = ({ config }) => {
  const { updateConfigSection } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [showClaudeKey, setShowClaudeKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  
  // Initialize form state from config for Claude
  const [claudeConfig, setClaudeConfig] = useState({
    apiKey: config.claude?.apiKey || '',
    model: config.claude?.model || 'claude-3-haiku-20240307',
    maxTokens: config.claude?.maxTokens || 64,
    baseURL: config.claude?.baseURL || 'https://api.anthropic.com/v1/messages',
    promptFile: config.claude?.promptFile || '/opt/smart-hub/hub/prompts/claude.txt',
  });

  // Initialize form state from config for OpenAI
  const [openAIConfig, setOpenAIConfig] = useState({
    apiKey: config.tts?.apiKey || '',
    voiceId: config.tts?.voiceId || 'G7ILShrCNLfmS0A37SXS',
    modelId: config.tts?.modelId || 'eleven_multilingual_v2',
    stability: config.tts?.voiceSettings?.stability || 0.1,
    similarityBoost: config.tts?.voiceSettings?.similarity_boost || 0.6,
  });
  // Handle Claude form changes
  const handleClaudeChange = (e) => {
    const { name, value } = e.target;
    setClaudeConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle OpenAI form changes
  const handleOpenAIChange = (e) => {
    const { name, value, type } = e.target;
    setOpenAIConfig(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };
  
  // Handle Claude form submit
  const handleClaudeSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    
    try {
      await updateConfigSection('claude', claudeConfig);
      setStatus({
        type: 'success',
        message: 'Claude AI settings updated successfully.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update Claude AI settings.'
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Handle OpenAI form submit
  const handleOpenAISubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    
    try {
      await updateConfigSection('tts', openAIConfig);
      setStatus({
        type: 'success',
        message: 'OpenAI settings updated successfully.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update OpenAI settings.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>AI Services Configuration</h2>
      <p>Configure AI services for natural language processing and smart home control.</p>
      
      {/* Claude AI Settings */}
      <div className="settings-section">
        <h3>Claude AI Settings</h3>
        <form onSubmit={handleClaudeSubmit}>
          <div className="form-group password-field">
            <label htmlFor="claude-apiKey">API Key</label>
            <input
              type={showClaudeKey ? "text" : "password"}
              id="claude-apiKey"
              name="apiKey"
              value={claudeConfig.apiKey}
              onChange={handleClaudeChange}
              required
            />
            <button 
              type="button"
              className="toggle-password"
              onClick={() => setShowClaudeKey(!showClaudeKey)}
            >
              {showClaudeKey ? "Hide" : "Show"}
            </button>
            <div className="form-help">
              API key for Claude.ai. Keep this secure and don't share it.
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="claude-model">Model</label>
            <select
              id="claude-model"
              name="model"
              value={claudeConfig.model}
              onChange={handleClaudeChange}
            >
              <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
              <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
              <option value="claude-3-opus-20240229">Claude 3 Opus</option>
              <option value="claude-2.0">Claude 2</option>
              <option value="claude-instant-1.2">Claude Instant</option>
            </select>
            <div className="form-help">
              Claude model to use for AI responses. More capable models use more tokens.
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="claude-maxTokens">Max Tokens</label>
              <input
                type="number"
                id="claude-maxTokens"
                name="maxTokens"
                value={claudeConfig.maxTokens}
                onChange={handleClaudeChange}
                min="16"
                max="4096"
              />
              <div className="form-help">
                Maximum tokens in responses. Higher values allow longer answers.
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="claude-baseURL">API Base URL</label>
              <input
                type="url"
                id="claude-baseURL"
                name="baseURL"
                value={claudeConfig.baseURL}
                onChange={handleClaudeChange}
              />
              <div className="form-help">
                Base URL for Claude API. Only change if using a custom endpoint.
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="claude-promptFile">System Prompt File</label>
            <input
              type="text"
              id="claude-promptFile"
              name="promptFile"
              value={claudeConfig.promptFile}
              onChange={handleClaudeChange}
            />
            <div className="form-help">
              Path to file containing the system prompt for Claude.
            </div>
          </div>
          
          <div className="form-buttons">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Claude Settings'}
            </button>
          </div>
        </form>
      </div>
      
      <div className="section-divider"></div>
      
      {/* OpenAI Settings */}
      <div className="settings-section">
        <h3>ElevenLabs TTS Settings</h3>
        <form onSubmit={handleOpenAISubmit}>
          <div className="form-group password-field">
            <label htmlFor="elevenlabs-apiKey">API Key</label>
            <input
              type={showOpenAIKey ? "text" : "password"}
              id="elevenlabs-apiKey"
              name="apiKey"
              value={openAIConfig.apiKey}
              onChange={handleOpenAIChange}
              required
            />
            <button 
              type="button"
              className="toggle-password"
              onClick={() => setShowOpenAIKey(!showOpenAIKey)}
            >
              {showOpenAIKey ? "Hide" : "Show"}
            </button>
            <div className="form-help">
              API key for ElevenLabs. Required for speech synthesis.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="voiceId">Voice ID</label>
            <input
              type="text"
              id="voiceId"
              name="voiceId"
              value={openAIConfig.voiceId || ''}
              onChange={handleOpenAIChange}
              placeholder="e.g., G7ILShrCNLfmS0A37SXS"
            />
            <div className="form-help">
              The voice ID from ElevenLabs dashboard.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="modelId">Model ID</label>
            <select
              id="modelId"
              name="modelId"
              value={openAIConfig.modelId || 'eleven_multilingual_v2'}
              onChange={handleOpenAIChange}
            >
              <option value="eleven_multilingual_v2">Multilingual v2</option>
              <option value="eleven_monolingual_v1">Monolingual v1</option>
              <option value="eleven_turbo_v2">Turbo v2 (fast)</option>
            </select>
            <div className="form-help">
              Choose which model ElevenLabs should use to generate speech.
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stability">Stability</label>
              <input
                type="number"
                id="stability"
                name="stability"
                value={openAIConfig.stability || 0.1}
                onChange={handleOpenAIChange}
                step="0.1"
                min="0"
                max="1"
              />
              <div className="form-help">
                Lower = more expressive, higher = more consistent.
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="similarityBoost">Similarity Boost</label>
              <input
                type="number"
                id="similarityBoost"
                name="similarityBoost"
                value={openAIConfig.similarityBoost || 0.6}
                onChange={handleOpenAIChange}
                step="0.1"
                min="0"
                max="1"
              />
              <div className="form-help">
                Boosts similarity to original voice.
              </div>
            </div>
          </div>

          {status && (
            <div className={`form-status ${status.type}`}>
              {status.message}
            </div>
          )}

          <div className="form-buttons">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save ElevenLabs Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AISettings;